import { CurrencyPipe, DecimalPipe, NgClass } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { IntegerDirective } from '@app/shared/directives/integer.directive';
import { MoneyDirective } from '@app/shared/directives/money.directive';
import { Rateio, ROUND_MODES, RoundType } from '@app/shared/enums/apportionment';
import { firstValueFrom, skipWhile } from 'rxjs';
import { A11yModule } from "@angular/cdk/a11y";
import { PaymentCondition } from '@app/interface/paymentCondition';
import { MatSelectModule } from '@angular/material/select';
import { Finance, PreviewTitle } from '@app/interface/ro-finance';
import { CashTitleService } from '@app/service/cash-title.service';
import { PAYMENT_METHODS } from '@app/shared/lists/payment-methods';
import { Parcel } from '@app/interface/parcel';

interface DataType {
  FIRST: number,
  FIRST_ONE: number,
  LAST_ONE: number,
  LAST: number,
}

@Component({
  selector: 'mkg-apportionment-preview',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MoneyDirective,
    IntegerDirective,
    MatTableModule,
    MatIconModule,
    CurrencyPipe,
    DecimalPipe,
    A11yModule,
    NgClass
],
  templateUrl: './apportionment-preview.component.html',
  styleUrl: './apportionment-preview.component.scss'
})
export class ApportionmentPreviewComponent {
  protected form = new FormGroup({
    total: new FormControl<number | null>(100, [Validators.required, Validators.min(0.01)]),
    parcels: new FormControl<number | null>(7, [Validators.required, Validators.min(1), Validators.max(20)]),
    roundType: new FormControl<RoundType | null>('round'),
    condition: new FormControl<PaymentCondition | null>(null)
  });
  protected table: DataType[] = [];
  protected columns = ['FIRST_ONE', 'FIRST', 'LAST_ONE', 'LAST']
  protected defaultValue: number = 0;
  protected difference: number = 0;
  protected realDivision: number = 0;
  protected grossProduct: number = 0;
  private _cashTitleService = inject(CashTitleService);
  readonly ROUND_MODES = ROUND_MODES;

  constructor(
    @Inject(MAT_DIALOG_DATA) protected paymentConditions: PaymentCondition[]
  ) { }


  ngOnInit(): void {
    this.form.valueChanges
      .pipe(skipWhile(() => this.form.invalid))
      .subscribe(formValue => {
        this.realDivision = formValue.total / formValue.parcels;
        this._simulate();
      })
    this._simulate();
  }

  ramdomizeFinance() {
    const amount = Math.ceil(Math.random() * 15);
    const parcels: Parcel[] = [];

    for (let index = 1; index <= amount; index++) {
      const sum = parcels.reduce((acc, p) => acc + p.percentage, 0);
      if (sum >= 100) break;
      let percentage: number
      if (index == amount) {
        percentage = 100 - sum;
      } else {
        percentage = Math.ceil(Math.random() * (100 - sum));
      }
      parcels.push({
        days: index,
        seq: index,
        percentage
      });
    }

    const condition: PaymentCondition = {
      available: 1,
      condition: "Aleatório",
      form: PAYMENT_METHODS.other,
      description: `Aleatórias\n(${parcels.map(p => `${p.percentage}%`).join(' + ')})`,
      parcels
    }

    this.paymentConditions.push(condition);
    this.form.get('condition').setValue(condition);
  }

  private async _simulateByCondition() {
    const { total, condition, roundType } = this.form.value;
    const finance = new Finance({
      paymentCondition: condition._id,
      value: total,
      jsonAny: JSON.stringify(condition.parcels)
    }, condition);
    const titles = await this.createTitles(roundType);
    const rateio = new Rateio({ array: titles, expectedSum: total, property: 'value' }).test();
    this.defaultValue = rateio.defaultValue;
    this.difference = Math.abs(rateio.difference);
    this.grossProduct = rateio.defaultValue * condition.parcels.length

    const table: DataType[] = [];
    for (let i = 0; i < condition.parcels.length; i++) {
      if (!rateio.table[i]) continue;
      table.push({
        FIRST: rateio.table[i].first,
        FIRST_ONE: rateio.table[i].firstOne,
        LAST_ONE: rateio.table[i].lastOne,
        LAST: rateio.table[i].last
      })
    }

    this.table = table;
  }


  private _simulate() {
    const { total, parcels, roundType, condition } = this.form.value;

    if (total && condition) {
      this._simulateByCondition();
      return
    }

    const arr = new Array(parcels);
    let roundedValue = 0;
    switch (roundType) {
      case 'ceil':
        roundedValue = Math.ceil(total / parcels * 100) / 100;
        break;
      case 'floor':
        roundedValue = Math.floor(total / parcels * 100) / 100;
        break;
      case 'round':
      default:
        roundedValue = Math.round(total / parcels * 100) / 100;
        break;
    }
    for (let i = 0; i < arr.length; i++) {
      arr[i] = {
        id: i + 1,
        value: roundedValue
      }
    }
    const rateio = new Rateio({ array: arr, expectedSum: total, property: 'value' }).test();
    this.defaultValue = rateio.defaultValue;
    this.difference = Math.abs(rateio.difference);
    this.grossProduct = rateio.defaultValue * parcels;

    const table: DataType[] = [];
    for (let i = 0; i < parcels; i++) {
      table.push({
        FIRST: rateio.table[i].first,
        FIRST_ONE: rateio.table[i].firstOne,
        LAST_ONE: rateio.table[i].lastOne,
        LAST: rateio.table[i].last,
      })
    }

    this.table = table;
  }

  // **criar títulos**
  // função que recebe o Finance, o valor e o método de arredendamento e cria os títulos
  async createTitles(roundType: RoundType) : Promise<PreviewTitle[]>{
    const { total, condition } = this.form.value;
    const finance = new Finance({
      paymentCondition: condition._id,
      value: total,
      jsonAny: JSON.stringify(condition.parcels)
    }, condition);
    const paymentCondition = finance.paymentCondition;
    const totalValue = finance.value;
    if (!paymentCondition || !totalValue) {
      return [];
    }
    let titles: PreviewTitle[] = [];

    if (paymentCondition.form === PAYMENT_METHODS.credit || paymentCondition.form === PAYMENT_METHODS.debit) {
      titles.push({
        parcel: 1,
        percentage: 100,
        value: Number(totalValue.toFixed(2)),
        expiration: new Date(),
        condition: paymentCondition
      });
    } else {
      let parcels = paymentCondition.parcels;
      // se a forma de pagamento é 999, usa o jsonAny
      if (paymentCondition.form === PAYMENT_METHODS.other) {
        const parcels999 = JSON.parse(finance.jsonAny ?? "[]");
        if (Array.isArray(parcels999) && parcels999.length) {
          parcels = parcels999;
        }
      }

      for (const parcel of parcels) {
        let value = parcel.value;
        if (!value) {
          if (this.areParcelsEquals(parcels)) {
            value = totalValue / parcels.length;
          } else {
            value = totalValue * parcel.percentage /100;
          }
        }

        switch (roundType) {
          case 'ceil':
            value = Math.ceil(value * 100) / 100;
            break;
          case 'floor':
            value = Math.floor(value * 100) / 100;
            break;
          case 'round':
          default:
            value = Math.round(value * 100) / 100;
            break;
        }

        let expiration = new Date(parcel.expirationDate || parcel["expiration"]);
        
        if(isNaN(expiration.valueOf())) { // invalid Date
          expiration = await firstValueFrom(this._cashTitleService.getExpirationDate(parcel.days));
        } 

        const title: PreviewTitle = {
          parcel: parcel.seq,
          percentage: parcel.percentage,
          value,
          expiration,
          condition: paymentCondition
        };
        titles.push(title);
      }
    }

    return titles;

  }

  areParcelsEquals(parcels: Parcel[]): boolean {
    switch (parcels.length) {
      case 0:
      case 1:
        return true;
      case 2:
        return parcels[0].percentage === parcels[1].percentage;
      default:
        const min = Math.floor(100 / parcels.length);
        const max = Math.ceil(100 / parcels.length);
        return parcels.every(p => p.percentage > min && p.percentage < max);
    }
  }

}




